import React, { useState } from "react";
import { Trash2 } from "lucide-react";
import Swal from "sweetalert2";

import AnimatedCartButton from "../CartButton";
import { useWishlist } from "../../hooks/useWishlist";
import { resolveImageUrl } from "../../utilities/Common";
import fallbackImg from "../../images/broken-watch.png";

interface WishlistRowProps {
  product: any;
  /** Opens the product page — the modal closes first (passed in by WishlistModal). */
  onOpenProduct: () => void;
  /** Re-read the wishlist after a removal. */
  onChanged: () => void | Promise<void>;
}

/**
 * One wishlist line, laid out horizontally (thumb | details | actions)
 * rather than as a grid tile.
 */
const WishlistRow: React.FC<WishlistRowProps> = ({ product, onOpenProduct, onChanged }) => {
  const { removeFromWishlist } = useWishlist();
  const [busy, setBusy] = useState(false);

  const mappingCode = String(product.sellerPriceDTO?.mappingCode ?? product.mappingCode ?? "");
  const price = Number(product.price) || 0;
  const mrp = Number(product.mrp) || 0;
  const discountPercent = mrp > price ? Math.round(((mrp - price) / mrp) * 100) : 0;
  const isOutOfStock = Boolean(product.isStockOut ?? product.isOutOfStock);
  const imageUrl = resolveImageUrl(product.image) || String(fallbackImg);

  const handleRemove = async () => {
    const result = await Swal.fire({
      title: "Remove from Wishlist?",
      text: `Remove "${product.name}" from your wishlist?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "var(--theme-error)",
      cancelButtonColor: "var(--theme-text-muted)",
      confirmButtonText: "Yes, remove",
    });
    if (!result.isConfirmed) return;

    setBusy(true);
    try {
      const apiRes = await removeFromWishlist(mappingCode);
      if (!apiRes || !apiRes.success) {
        await Swal.fire({
          icon: "error",
          title: "Failed",
          text: "Could not remove from wishlist.",
          timer: 1500,
          showConfirmButton: false,
        });
        return;
      }
      await onChanged();
    } catch {
      /* the row stays as it was */
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`wl-row${isOutOfStock ? " wl-row--oos" : ""}`}>
      {busy && (
        <div className="wl-row__busy">
          <span className="wl-row__spinner" />
        </div>
      )}

      <button type="button" className="wl-row__thumb" onClick={onOpenProduct}>
        <img src={imageUrl} alt={product.name} loading="lazy" />
      </button>

      <div className="wl-row__body">
        <div className="wl-row__head">
          <button type="button" className="wl-row__name" onClick={onOpenProduct}>
            {product.name}
          </button>
          <button
            type="button"
            className="wl-row__remove"
            onClick={handleRemove}
            disabled={busy}
            aria-label={`Remove ${product.name} from wishlist`}
          >
            <Trash2 size={15} />
          </button>
        </div>

        {isOutOfStock && <span className="wl-row__oos">Out of stock</span>}

        <div className="wl-row__price">
          <span className="wl-row__price-now">₹{price.toLocaleString("en-IN")}</span>
          {mrp > price && (
            <>
              <span className="wl-row__price-mrp">₹{mrp.toLocaleString("en-IN")}</span>
              <span className="wl-row__off">{discountPercent}% OFF</span>
            </>
          )}
        </div>

        <div className="wl-row__action">
          <AnimatedCartButton
            mappingCode={mappingCode}
            variationCode={String(product.sellerPriceDTO?.variationCode || product.variationCode || "")}
            initialInCart={Boolean(product.sellerPriceDTO?.isInCart ?? product.isInCart)}
            availableStock={product.availableStock}
            disabled={isOutOfStock}
          />
        </div>
      </div>
    </div>
  );
};

export default WishlistRow;
